import * as angular from "angular";
import {IController, IWindowService} from "angular";
import * as _ from "lodash";
import {finalize} from "rxjs";
import {VisitPlanModel} from "../../models/visit-plan.model";
import {VisitPlanStatusEnum} from "../../enums/visit-plan-status.enum";
import {SchedulingProjectStatusEnum} from "../../enums/scheduling-project-status.enum";
import {VisitPlanHttpService} from "../../angular/services/visit-plan-http.service";
import {VisitPlanFacadeService} from "../../angular/services/visit-plan-facade.service";
import {SchedulingProjectDialogService} from "../../services/scheduling-project-dialog.service";
import {ConfirmDialogService} from "../../../commons/services/confirm-dialog.service";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {SchedulingProjectAlertsService} from "../../services/scheduling-project-alerts.service";
import {DateRangeTimeFiltersService} from "../../../commons/services/date-range-time-filters.service";
import {SiteContextService} from "../../../commons/services/site-context.service";
import {SplitService} from "../../../commons/services/split-service.service";
import {SchedulingProjectFilterCascadeService} from "../../services/scheduling-project-filter-cascade.service";
import {VisitPlanFilterSettingsService} from "../../services/visit-plan-filter-settings.service";
import {CommonsService} from "../../../commons/services/commons-service.service";
import {VisitPlanFilterModel} from "../../models/visit-plan-filter.model";
import {SortType} from "../../../commons/types/sort.type";

export class SchedulingProjectVisitPlanTableViewComponent implements IController {
  from: number;
  to: number;
  branchIds: string[];
  groupIds: string[];
  tags: string[];
  serviceAreaIds: string[];
  statuses: VisitPlanStatusEnum[];
  templateIds?: string[];
  schedulingProjectStatuses: SchedulingProjectStatusEnum[];

  public isArchive: boolean = false;
  public createdByCurrentUser: boolean = false;
  public viewType: string = '/table';
  public filterConfig: any;
  public filteredBranchIds: string[];
  public filteredGroupIds: string[];
  public infiniteScroll: any;
  public schedulingProjectOptions: string[];
  public selectedSortValue: { name: string, type: SortType };

  private readonly loadLimit: number = 30;
  // Server-side (Mongo) field paths of the sortable table columns
  private readonly sortFields: { [sortName: string]: string } = {
    sortName: 'name',
    sortTemplate: 'schedulingProjectTemplateReference.name',
    sortStatus: 'schedulingProjectStatus',
    sortDates: 'from',
    sortServiceTimeProvider: 'visitPlanConfig.visitSlotProviderTypeRegistry.visitSlotServiceTimeProviderType',
    sortUnitsProvider: 'visitPlanConfig.visitSlotProviderTypeRegistry.visitSlotUnitsProviderType',
    sortDeliveryTimeProvider: 'visitPlanConfig.visitSlotProviderTypeRegistry.visitSlotDeliveryTimeProviderType',
    sortCreatedBy: 'createdBy.name'
  };
  private visitPlansRef: any;

  /* @ngInject */
  constructor(private visitPlanHttpService: VisitPlanHttpService,
              private visitPlanFacadeService: VisitPlanFacadeService,
              private schedulingProjectDialogService: SchedulingProjectDialogService,
              private confirmDialogService: ConfirmDialogService, private commonsFacadeService: CommonsFacadeService,
              private schedulingProjectAlertsService: SchedulingProjectAlertsService,
              private dateRangeTimeFiltersService: DateRangeTimeFiltersService, private siteContextService: SiteContextService,
              private splitService: SplitService, private IAMPermissions, private moment,
              private schedulingProjectFilterCascadeService: SchedulingProjectFilterCascadeService,
              private visitPlanFilterSettingsService: VisitPlanFilterSettingsService,
              private commonsService: CommonsService, private $window: IWindowService,
              private $timeout, private $infiniteScroll, private $state, private $log, private $q) {
  }

  $onInit() {
    this.branchIds = this.branchIds || [];
    this.groupIds = this.groupIds || [];
    this.tags = this.tags || [];
    this.serviceAreaIds = this.serviceAreaIds || [];
    this.statuses = this.statuses || [];
    this.templateIds = this.templateIds || [];
    this.schedulingProjectStatuses = this.schedulingProjectStatuses || [];
    this.schedulingProjectOptions = [SchedulingProjectStatusEnum.Active, SchedulingProjectStatusEnum.Planning, SchedulingProjectStatusEnum.Executing, SchedulingProjectStatusEnum.Closed];

    if (this.IAMPermissions.hasRole('super.owner')) {
      this.schedulingProjectOptions.unshift(SchedulingProjectStatusEnum.Importing);
    }

    let range: any = {};
    if (this.from && this.to) {
      range.dateStart = this.from;
      range.dateEnd = this.to;
    } else {
      range = this.dateRangeTimeFiltersService.thisYear();
    }

    this.filterConfig = {
      customTemplates: this.dateRangeTimeFiltersService.getDefaultCustomTemplates(),
      range: range
    };

    this.visitPlansRef = this.getVisitPlansRef();
    if (this.tags?.length) {
      this.infiniteScroll = this.$infiniteScroll(this.visitPlansRef, this.loadLimit, {getFullData: true});
      this._applyTagsFilterChange(true);
    } else {
      this.visitPlansRef.$query(this.getQueryPayload());
      this.infiniteScroll = this.$infiniteScroll(this.visitPlansRef, this.loadLimit, {getFullData: true});
    }
  }

  // $infiniteScroll exposes no explicit loadNext() - asking for the item at the first
  // not-yet-loaded index is its public way to trigger the cursor-based fetch, same as
  // md-virtual-repeat used to do internally via getItemAtIndex().
  onFetchNextVisitPlans() {
    this.infiniteScroll?.getItemAtIndex(this.infiniteScroll.size());
  }

  onChangeViewType(view: string) {
    if (this.viewType === view) return;
    this.viewType = view;
    this.$state.go(view === '/calendar' ? '^.calendar' : '^.table', this.getStateParams(), {notify: true, reload: false});
  }

  private _setStateParams() {
    this.$state.go('.', {
      from: this.filterConfig.range.dateStart,
      to: this.filterConfig.range.dateEnd
    }, {notify: false, reload: false, location: 'replace'});
  }

  submitQuery(queryString: string) {
    this.filterConfig.queryString = queryString;
    this.reloadVisitPlans();
  }

  onRangeChanged(range) {
    this.filterConfig.range = range;
    this._setStateParams();
    // this._saveFilterState();
    this.reloadVisitPlans();
  }

  onBranchesSelect(branchIds: string[]) {
    if (branchIds === this.branchIds) return;
    this.branchIds = branchIds;
    this._saveFilterState();
    this.reloadVisitPlans();
  }

  onTemplatesSelect(templateIds: string[]) {
    if (templateIds === this.templateIds) return;
    this.templateIds = templateIds;
    this._saveFilterState();
    this.reloadVisitPlans();
  }

  onTagsSelect(tags: string[]) {
    if (_.isEqual(tags, this.tags)) return;
    this.tags = tags || [];
    this._applyTagsFilterChange(true);
  }

  onGroupsSelect(groupIds: string[]) {
    if (_.isEqual(groupIds, this.groupIds)) return;
    this.groupIds = groupIds;

    if (groupIds?.length) {
      this._applyBranchesFromGroups(groupIds, true);
    } else {
      this.filteredBranchIds = undefined;
      this.branchIds = [];
      this._saveFilterState();
      this.reloadVisitPlans();
    }
  }

  onChangeSchedulingProjectStatuses(schedulingProjectStatuses: SchedulingProjectStatusEnum[]) {
    this.schedulingProjectStatuses = schedulingProjectStatuses || [];
    this._saveFilterState();
    this.reloadVisitPlans();
  }

  onSortChange(sortName: string) {
    const isSameSortName = this.selectedSortValue?.name === sortName;
    this.selectedSortValue = {
      name: sortName,
      type: isSameSortName && this.selectedSortValue.type === SortType.asc ? SortType.desc : SortType.asc
    };
    this.reloadVisitPlans();
  }

  onChangeIsArchive(isArchive: boolean) {
    this.isArchive = isArchive;
    this._saveFilterState();
    this.reloadVisitPlans();
  }

  onChangeCreatedByCurrentUser(createdByCurrentUser: boolean) {
    this.createdByCurrentUser = !!createdByCurrentUser;
    this._saveFilterState();
    this.reloadVisitPlans();
  }

  onGoToVisitPlan(visitPlan: VisitPlanModel) {
    this.$state.go('scheduling-project.visit-plan', {id: visitPlan.id}, {notify: true, reload: false});
  }

  onVisitPlanActions(visitPlan: VisitPlanModel, $event) {
    const actions = [];

    if (visitPlan?.isArchive) {
      if (this.IAMPermissions.hasPermission('schedulingProject.visitPlan.delete')) {
        actions.push('Restore');
      }
    } else {
      actions.push('Enter');

      if (this.IAMPermissions.hasPermission('schedulingProject.visitPlan.update')) {
        actions.push('Edit', 'Open in new tab', 'Delete');
      }

      if (this.IAMPermissions.hasPermission('schedulingProject.visitPlan.create')) {
        actions.push('Duplicate');
      }
    }

    if (!actions.length) {
      return;
    }

    this.commonsService.openActionPanel(actions, $event)
      .then((reason) => {
        if (reason === 'Enter') {
          this.onGoToVisitPlan(visitPlan);
        }
        if (reason === 'Open in new tab') {
          const url = this.$state.href('scheduling-project.visit-plan', {id: visitPlan.id});
          this.$window.open(url, '_blank');
        }
        if (reason === 'Edit') {
          this.onEditVisitPlan(visitPlan);
        }
        if (reason === 'Delete') {
          this.onDeleteVisitPlan(visitPlan);
        }
        if (reason === 'Restore') {
          this.onRestoreVisitPlan(visitPlan);
        }
        if (reason === 'Duplicate') {
          this.onCreateVisitPlan($event, visitPlan);
        }
      });
  }

  onCreateVisitPlan(event, sourceVisitPlan?: VisitPlanModel) {
    this.schedulingProjectDialogService.createVisitPlan(event, sourceVisitPlan)
      .then((visitPlan: VisitPlanModel) => {
        this.createVisitPlan(visitPlan);
      })
      .catch(error => this.$log.info('Dialog Closed', error));
  }

  onEditVisitPlan(visitPlan: VisitPlanModel) {
    if (!visitPlan) return;

    this.schedulingProjectDialogService.editVisitPlanName(visitPlan)
      .then((updatedVisitPlan: VisitPlanModel) => {
        this.commonsFacadeService.setLoaderOn();
        this.visitPlanHttpService.updateVisitPlan(updatedVisitPlan)
          .pipe(finalize(() => this.commonsFacadeService.setLoaderOff()))
          .subscribe({
            next: () => {
              this.schedulingProjectAlertsService.updateVisitPlanSuccess();
              this.reloadVisitPlans();
            },
            error: () => this.schedulingProjectAlertsService.updateVisitPlanError()
          });
      })
      .catch(error => this.$log.info('Dialog Closed', error));
  }

  onDeleteVisitPlan(visitPlan: VisitPlanModel) {
    const text = `Are you sure you want to delete the project ${visitPlan.name ? `"${visitPlan.name}"` : ""}?`;
    this.confirmDialogService.confirm('Delete Project', text, undefined)
      .then(() => {
        this.commonsFacadeService.setLoaderOn();
        this.visitPlanHttpService.deleteVisitPlan(visitPlan.id, false)
          .pipe(finalize(() => this.commonsFacadeService.setLoaderOff()))
          .subscribe({
            next: () => {
              this.schedulingProjectAlertsService.deleteSuccess();
              this.reloadVisitPlans();
            },
            error: () => this.schedulingProjectAlertsService.deleteError()
          });
      });
  }

  onRestoreVisitPlan(visitPlan: VisitPlanModel) {
    const text = `Are you sure you want to restore the project ${visitPlan.name ? `"${visitPlan.name}"` : ""}?`;
    this.confirmDialogService.confirm('Restore Project', text, undefined)
      .then(() => {
        this.commonsFacadeService.setLoaderOn();
        this.visitPlanHttpService.restoreVisitPlan(visitPlan.id)
          .pipe(finalize(() => this.commonsFacadeService.setLoaderOff()))
          .subscribe({
            next: () => {
              this.schedulingProjectAlertsService.restoreSuccess();
              this.reloadVisitPlans();
            },
            error: () => this.schedulingProjectAlertsService.restoreError()
          });
      });
  }

  private _applyTagsFilterChange(reload: boolean) {
    this.schedulingProjectFilterCascadeService.applyTagsFilter(this.tags)
      .then((state) => {
        this.filteredGroupIds = state.filteredGroupIds;
        this.groupIds = state.groupIds;
        this.filteredBranchIds = state.filteredBranchIds;
        this.branchIds = state.branchIds;
        this._saveFilterState();
        if (reload) {
          this.reloadVisitPlans();
        }
      })
      .catch((error) => {
        if (error?.stale) {
          return;
        }
        this.filteredGroupIds = undefined;
        this.groupIds = [];
        this.filteredBranchIds = undefined;
        this.branchIds = [];
        this._saveFilterState();
        if (reload) {
          this.reloadVisitPlans();
        }
      })
      .finally(() => this.$timeout(() => {}, 0));
  }

  private _applyBranchesFromGroups(groupIds: string[], reload: boolean) {
    this.schedulingProjectFilterCascadeService.applyBranchesFromGroups(groupIds)
      .then((state) => {
        this.filteredBranchIds = state.filteredBranchIds;
        this.branchIds = state.branchIds;
        this._saveFilterState();
        if (reload) {
          this.reloadVisitPlans();
        }
      })
      .catch((error) => {
        if (error?.stale) {
          return;
        }
        this.filteredBranchIds = undefined;
        this.branchIds = [];
        this._saveFilterState();
        if (reload) {
          this.reloadVisitPlans();
        }
      })
      .finally(() => this.$timeout(() => {}, 0));
  }

  private _saveFilterState() {
    this.visitPlanFilterSettingsService.setFilters(this.getStateParams());
  }

  private reloadVisitPlans() {
    if (!this.visitPlansRef || !this.infiniteScroll) return;
    this.visitPlansRef.$query(this.getQueryPayload())
      .then(() => this.infiniteScroll.reload())
      .catch((error) => this.$log.error('Failed to query scheduling projects', error));
  }

  private getVisitPlansRef() {
    const ref: any = {
      _cursor: undefined,
      _filter: undefined,
      _params: undefined,
      $query: (payload: { filter: VisitPlanFilterModel, params: any }) => {
        ref._filter = payload.filter;
        ref._params = payload.params;
        ref._cursor = undefined;
        return this.$q.resolve();
      },
      $next: () => {
        const deferred = this.$q.defer();
        const params: any = {...(ref._params || {})};
        if (ref._cursor) {
          params.cursor = ref._cursor;
        }

        this.visitPlanFacadeService.queryVisitPlansCursor(ref._filter || {}, params)
          .subscribe({
            next: (response) => {
              ref._cursor = response.cursor;
              deferred.resolve(response.list || []);
            },
            error: (error) => {
              this.$log.error('Failed to load scheduling projects', error);
              deferred.resolve([]);
            }
          });

        return deferred.promise;
      }
    };
    return ref;
  }

  private getQueryPayload(): { filter: VisitPlanFilterModel, params: any } {
    return {
      filter: this.getFilter(),
      params: {
        size: this.loadLimit,
        sort: this.selectedSortValue
          ? `${this.sortFields[this.selectedSortValue.name]},${this.selectedSortValue.type}`
          : 'id,desc'
      }
    };
  }

  private getFilter(): VisitPlanFilterModel {
    const filter: VisitPlanFilterModel = {
      from: this.filterConfig.range.dateStart,
      to: this.filterConfig.range.dateEnd,
      isArchive: this.isArchive,
      createdByCurrentUser: this.createdByCurrentUser
    };

    if (this.filterConfig.queryString) {
      filter.query = this.filterConfig.queryString;
    }
    if (!_.isEmpty(this.branchIds)) {
      filter.branchIds = this.branchIds;
    }
    if (!_.isEmpty(this.serviceAreaIds)) {
      filter.areaIds = this.serviceAreaIds;
    }
    if (!_.isEmpty(this.statuses)) {
      filter.statuses = this.statuses;
    }
    if (!_.isEmpty(this.templateIds)) {
      filter.templateIds = this.templateIds;
    }
    if (!_.isEmpty(this.schedulingProjectStatuses)) {
      filter.schedulingProjectStatuses = this.schedulingProjectStatuses;
    }

    return filter;
  }

  private getStateParams() {
    return {
      branchIds: this.branchIds,
      groupIds: this.groupIds,
      tags: this.tags,
      serviceAreaIds: this.serviceAreaIds,
      statuses: this.statuses,
      templateIds: this.templateIds,
      from: this.filterConfig.range.dateStart,
      to: this.filterConfig.range.dateEnd,
      schedulingProjectStatuses: this.schedulingProjectStatuses,
      isArchive: this.isArchive,
      createdByCurrentUser: this.createdByCurrentUser
    };
  }

  private createVisitPlan(visitPlan: VisitPlanModel) {
    this.commonsFacadeService.setLoaderOn();
    this.visitPlanHttpService.createVisitPlan(visitPlan)
      .pipe(finalize(() => this.commonsFacadeService.setLoaderOff()))
      .subscribe({
        next: (createdVisitPlan: VisitPlanModel) => {
          this.schedulingProjectAlertsService.createVisitPlanSuccess();
          this.$state.go('scheduling-project.visit-plan', {id: createdVisitPlan.id}, {notify: true, reload: false});
        },
        error: () => this.schedulingProjectAlertsService.createVisitPlanError()
      });
  }

}

angular
  .module('bringoz.admin.scheduling-project')
  .component('schedulingProjectVisitPlanTableView', {
    bindings: {
      from: '<',
      to: '<',
      branchIds: '<?',
      groupIds: '<?',
      tags: '<?',
      serviceAreaIds: '<?',
      statuses: '<?',
      templateIds: '<?',
      schedulingProjectStatuses: '<?',
      isArchive: '<?',
      createdByCurrentUser: '<?'
    },
    template: require('./scheduling-project-visit-plan-table-view.html').default,
    controller: SchedulingProjectVisitPlanTableViewComponent
  });
